import React, { useState, useEffect } from 'react';
import { 
  Container, 
  Typography, 
  Paper, 
  Box, 
  Button, 
  CircularProgress, 
  Alert,
  styled,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Avatar,
  TablePagination,
  TextField,
  InputAdornment,
  IconButton,
  useMediaQuery,
  useTheme,
  Grid
} from '@mui/material';
import PeopleIcon from '@mui/icons-material/People';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import PersonIcon from '@mui/icons-material/Person';
import EmailIcon from '@mui/icons-material/Email';
import PhoneIcon from '@mui/icons-material/Phone';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import SearchIcon from '@mui/icons-material/Search';
import { useNavigate } from 'react-router-dom';
import ownAuthSession from '../../../services/auth/ownAuthSession';
import firebaseDataService from '../../../services/FirebaseDataService';
import { mainApi } from '../../../config/apiClient';
import { useTranslation } from 'react-i18next';
import i18n from '../../../i18n';

// Müşteri Veri Tipi
interface Customer {
  id: number;
  firstName: string;
  lastName: string;
  email: string;
  phoneNumber?: string;
  firebaseUid?: string;
  isActive: boolean;
  lastLoginAt?: string;
  createdAt: string;
}

// Styled Components
const ScrollableContent = styled(Box)(({ theme }) => ({
  padding: theme.spacing(3),
  overflowY: 'auto', 
  height: 'calc(100vh - 64px)',
  '&::-webkit-scrollbar': {
    width: '8px',
  },
  '&::-webkit-scrollbar-track': {
    background: '#f1f1f1',
    borderRadius: '4px',
  },
  '&::-webkit-scrollbar-thumb': {
    background: '#888',
    borderRadius: '4px',
  },
  '&::-webkit-scrollbar-thumb:hover': {
    background: '#555',
  },
  // Mobil için responsive ayarlar
  '@media (max-width: 1499px)': {
    padding: theme.spacing(2),
    height: 'auto',
    overflowY: 'visible',
  },
}));

// CustomersPage arayüzü
interface CustomersPageProps {
  isManager?: boolean;
  onNavigateBack?: () => void;
}

const CustomersPage: React.FC<CustomersPageProps> = ({ isManager = false, onNavigateBack }) => {
  const { t } = useTranslation('salesSystem');
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery('(max-width: 1499px)');
  
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [filteredCustomers, setFilteredCustomers] = useState<Customer[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [companyId, setCompanyId] = useState<string | null>(null);
  
  // Sayfalama ve arama
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(isMobile ? 25 : 50);
  const [searchTerm, setSearchTerm] = useState('');

  // Şirket ID'sini al
  useEffect(() => {
    const fetchCompanyId = async () => {
      const uid = ownAuthSession.getCurrentUid();
      if (uid) {
        try {
          const userData = await firebaseDataService.getUserData(uid);
          if (userData?.companyId) {
            setCompanyId(userData.companyId);
          } else {
            setError(t('qrMenuCustomersPage.errors.userCompanyNotFound'));
            setLoading(false);
          }
        } catch (error) {
          console.error("Şirket ID'si alınırken hata:", error);
          setError(t('qrMenuCustomersPage.errors.companyIdLoadFailed'));
          setLoading(false);
        }
      } else {
        setError(t('qrMenuCustomersPage.errors.userNotLoggedIn'));
        setLoading(false);
      }
    };
    fetchCompanyId();
  }, []);

  // Müşterileri SQL'den yükle
  useEffect(() => {
    if (!companyId) return;

    const fetchCustomersFromSQL = async () => {
      setLoading(true);
      setError(null);
      
      try {
        console.log(`🔄 SQL'den müşteriler yükleniyor (Şirket: ${companyId})`);
        
        const response = await mainApi.get(`/api/Users/customers`);
        const result = response.data;
        if (result.success && result.data) {
          console.log(`✅ SQL'den ${result.data.length} müşteri yüklendi`);
          setCustomers(result.data);
          setFilteredCustomers(result.data);
        } else {
          console.log('ℹ️ SQL\'de henüz müşteri bulunamadı');
          setCustomers([]);
          setFilteredCustomers([]);
        }
      } catch (err: any) {
        console.error('❌ SQL müşteri yükleme hatası:', err);
        if (err.response?.status === 404) {
          console.log('ℹ️ Şirket henüz SQL\'de kayıtlı değil, boş liste gösteriliyor');
          setCustomers([]);
          setFilteredCustomers([]);
        } else {
          setError(t('qrMenuCustomersPage.errors.loadFailed'));
        }
      } finally {
        setLoading(false);
      }
    };

    fetchCustomersFromSQL();
  }, [companyId]);

  // Arama fonksiyonu
  useEffect(() => {
    if (!searchTerm.trim()) {
      setFilteredCustomers(customers);
    } else {
      const filtered = customers.filter(customer =>
        customer.firstName.toLowerCase().includes(searchTerm.toLowerCase()) ||
        customer.lastName.toLowerCase().includes(searchTerm.toLowerCase()) ||
        customer.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (customer.phoneNumber && customer.phoneNumber.includes(searchTerm))
      );
      setFilteredCustomers(filtered);
    }
    setPage(0); // Arama yapıldığında ilk sayfaya dön
  }, [searchTerm, customers]);

  const handleGoBack = () => {
    isManager && onNavigateBack ? onNavigateBack() : navigate('/qr-menu-management');
  };

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(event.target.value);
  };

  const formatDate = (dateString: string) => {
    const locale = i18n.language?.startsWith('tr') ? 'tr-TR' : 'en-US';
    return new Date(dateString).toLocaleDateString(locale, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getInitials = (firstName: string, lastName: string) => {
    return `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase();
  };

  // Sayfalanmış veriler
  const paginatedCustomers = filteredCustomers.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  return (
    <ScrollableContent>
      {/* 📱 MOBİL HEADER */}
      {isMobile ? (
        <Box sx={{ mb: 2 }}>
          {/* Üst satır: Geri butonu, başlık ve toplam chip */}
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <IconButton
                onClick={handleGoBack}
                sx={{ 
                  bgcolor: 'grey.100', 
                  '&:hover': { bgcolor: 'grey.200' } 
                }}
              >
                <ArrowBackIcon />
              </IconButton>
              <PeopleIcon sx={{ fontSize: 24, color: 'primary.main' }} />
              <Typography variant="h6" component="h1" fontWeight="bold">
                {t('qrMenuCustomersPage.titleShort')}
              </Typography>
            </Box>
            <Chip 
              label={t('qrMenuCustomersPage.mobile.totalCustomers', { count: customers.length })}
              color="primary"
              size="small"
            />
          </Box>
          
          {/* Alt satır: Arama kutusu */}
          <TextField
            size="small"
            variant="outlined"
            placeholder={t('qrMenuCustomersPage.searchPlaceholder')}
            value={searchTerm}
            onChange={handleSearchChange}
            fullWidth
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ fontSize: 18 }} color="action" />
                </InputAdornment>
              ),
            }}
            sx={{ 
              '& .MuiOutlinedInput-root': {
                height: 40
              }
            }}
          />
          
          {/* Filtrelenen sonuç sayısı */}
          {searchTerm && (
            <Box sx={{ mt: 1 }}>
              <Chip 
                label={t('qrMenuCustomersPage.mobile.searchResults', { count: filteredCustomers.length })}
                color="secondary"
                variant="outlined"
                size="small"
              />
            </Box>
          )}
        </Box>
      ) : (
        /* 💻 MASAÜSTÜ HEADER */
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Button
              startIcon={<ArrowBackIcon />}
              onClick={handleGoBack}
              variant="outlined"
            >
              {t('qrMenuCustomersPage.actions.back')}
            </Button>
            <PeopleIcon sx={{ fontSize: 32, color: 'primary.main' }} />
            <Typography variant="h5" component="h1" fontWeight="bold">
              {t('qrMenuCustomersPage.title')}
            </Typography>
            
            {/* Arama Kutusu - Başlığın yanında */}
            <TextField
              size="small"
              variant="outlined"
              placeholder={t('qrMenuCustomersPage.searchPlaceholder')}
              value={searchTerm}
              onChange={handleSearchChange}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ fontSize: 18 }} color="action" />
                  </InputAdornment>
                ),
              }}
              sx={{ 
                width: 280,
                '& .MuiOutlinedInput-root': {
                  height: 36
                }
              }}
            />
          </Box>
          
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Chip 
              label={t('qrMenuCustomersPage.desktop.totalCustomers', { count: customers.length })}
              color="primary"
              variant="outlined"
            />
            {searchTerm && (
              <Chip 
                label={t('qrMenuCustomersPage.desktop.filteredCustomers', { count: filteredCustomers.length })}
                color="secondary"
                variant="outlined"
                size="small"
              />
            )}
          </Box>
        </Box>
      )}

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {/* Müşteriler Tablosu */}
      <Typography variant="h6" gutterBottom sx={{ mt: 3, fontWeight: 'bold', display: 'flex', alignItems: 'center' }}>
        <PersonIcon sx={{ mr: 1 }} /> {t('qrMenuCustomersPage.registeredCustomers')}
      </Typography>
      
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '400px' }}>
          <CircularProgress size={60} />
        </Box>
      ) : filteredCustomers.length === 0 && !error ? (
        <Paper elevation={3} sx={{ p: 4, textAlign: 'center', mt: 2 }}>
          <PeopleIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
          <Typography variant="h6" color="text.secondary" gutterBottom>
            {t('qrMenuCustomersPage.emptyState.title')}
          </Typography>
          <Typography color="text.secondary">
            {searchTerm
              ? t('qrMenuCustomersPage.emptyState.searchNoResults')
              : t('qrMenuCustomersPage.emptyState.description')}
          </Typography>
        </Paper>
      ) : !error ? (
        <>
        {/* 💻 MASAÜSTÜ: Tablo Görünümü */}
        {!isMobile && (
          <Paper elevation={3} sx={{ borderRadius: 2, overflow: 'hidden', mt: 2 }}>
            <TableContainer sx={{ maxHeight: 500 }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 'bold', bgcolor: 'primary.main', color: 'white', width: '50px', p: 1 }}>
                      
                    </TableCell>
                    <TableCell sx={{ fontWeight: 'bold', bgcolor: 'primary.main', color: 'white', minWidth: '180px' }}>
                      {t('qrMenuCustomersPage.table.customerInfo')}
                    </TableCell>
                    <TableCell sx={{ fontWeight: 'bold', bgcolor: 'primary.main', color: 'white', minWidth: '200px' }}>
                      {t('qrMenuCustomersPage.table.contact')}
                    </TableCell>
                    <TableCell sx={{ fontWeight: 'bold', bgcolor: 'primary.main', color: 'white', width: '80px' }}>
                      {t('qrMenuCustomersPage.table.status')}
                    </TableCell>
                    <TableCell sx={{ fontWeight: 'bold', bgcolor: 'primary.main', color: 'white', width: '120px' }}>
                      {t('qrMenuCustomersPage.table.registrationDate')}
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {paginatedCustomers.map((customer) => (
                    <TableRow 
                      key={customer.id}
                      hover
                      sx={{ '&:nth-of-type(odd)': { bgcolor: 'action.hover' } }}
                    >
                      <TableCell sx={{ p: 1 }}>
                        <Avatar 
                          sx={{ 
                            bgcolor: customer.isActive ? 'primary.main' : 'grey.500',
                            width: 32, 
                            height: 32,
                            fontSize: '0.75rem'
                          }}
                        >
                          {getInitials(customer.firstName, customer.lastName)}
                        </Avatar>
                      </TableCell>
                      <TableCell>
                        <Box>
                          <Typography variant="body2" fontWeight="medium" sx={{ mb: 0.5 }}>
                            {customer.firstName} {customer.lastName}
                          </Typography>
                          {customer.firebaseUid && (
                            <Typography variant="caption" color="text.secondary">
                              ID: {customer.firebaseUid.substring(0, 8)}...
                            </Typography>
                          )}
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Box>
                          <Typography variant="body2" sx={{ mb: 0.5 }}>
                            {customer.email}
                          </Typography>
                          {customer.phoneNumber && (
                            <Typography variant="caption" color="text.secondary">
                              {customer.phoneNumber}
                            </Typography>
                          )}
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Chip 
                          label={customer.isActive
                            ? t('qrMenuCustomersPage.status.active')
                            : t('qrMenuCustomersPage.status.passive')}
                          color={customer.isActive ? 'success' : 'default'}
                          size="small"
                          variant="outlined"
                        />
                      </TableCell>
                      <TableCell>
                        <Typography variant="caption">
                          {formatDate(customer.createdAt)}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            
            {/* Sayfalama - Masaüstü */}
            <TablePagination
              component="div"
              count={filteredCustomers.length}
              page={page}
              onPageChange={handleChangePage}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              rowsPerPageOptions={[10, 25, 50, 100]}
              labelRowsPerPage={t('qrMenuCustomersPage.pagination.rowsPerPage')}
              labelDisplayedRows={({ from, to, count }) => 
                `${from}-${to} / ${count !== -1 ? count : t('qrMenuCustomersPage.pagination.moreThan', { to })}`
              }
              sx={{
                borderTop: 1,
                borderColor: 'divider',
                '& .MuiTablePagination-toolbar': {
                  minHeight: 52
                }
              }}
            />
          </Paper>
        )}
        
        {/* 📱 MOBİL: Kart Görünümü */}
        {isMobile && (
          <Box sx={{ mt: 2 }}>
            {paginatedCustomers.map((customer) => (
              <Paper 
                key={customer.id} 
                elevation={2} 
                sx={{ 
                  mb: 1.5, 
                  borderRadius: 2, 
                  overflow: 'hidden',
                  border: '1px solid',
                  borderColor: customer.isActive ? 'divider' : 'grey.400',
                }}
              >
                {/* Üst Kısım: Avatar + İsim + Durum */}
                <Box 
                  sx={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    justifyContent: 'space-between',
                    p: 1.5, 
                    bgcolor: 'grey.50',
                    borderBottom: '1px solid',
                    borderColor: 'divider'
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <Avatar 
                      sx={{ 
                        bgcolor: customer.isActive ? 'primary.main' : 'grey.500',
                        width: 40, 
                        height: 40,
                        fontSize: '0.85rem'
                      }}
                    >
                      {getInitials(customer.firstName, customer.lastName)}
                    </Avatar>
                    <Box>
                      <Typography variant="body1" fontWeight="bold">
                        {customer.firstName} {customer.lastName}
                      </Typography>
                      {customer.firebaseUid && (
                        <Typography variant="caption" color="text.secondary">
                          ID: {customer.firebaseUid.substring(0, 8)}...
                        </Typography>
                      )}
                    </Box>
                  </Box>
                  <Chip 
                    label={customer.isActive
                      ? t('qrMenuCustomersPage.status.active')
                      : t('qrMenuCustomersPage.status.passive')}
                    color={customer.isActive ? 'success' : 'default'}
                    size="small"
                  />
                </Box>
                
                {/* Alt Kısım: İletişim Bilgileri */}
                <Box sx={{ p: 1.5 }}>
                  <Grid container spacing={1}>
                    <Grid item xs={12}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <EmailIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                        <Typography variant="body2" noWrap>
                          {customer.email}
                        </Typography>
                      </Box>
                    </Grid>
                    {customer.phoneNumber && (
                      <Grid item xs={6}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <PhoneIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                          <Typography variant="body2">
                            {customer.phoneNumber}
                          </Typography>
                        </Box>
                      </Grid>
                    )}
                    <Grid item xs={customer.phoneNumber ? 6 : 12}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <CalendarTodayIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                        <Typography variant="caption" color="text.secondary">
                          {formatDate(customer.createdAt)}
                        </Typography>
                      </Box>
                    </Grid>
                  </Grid>
                </Box>
              </Paper>
            ))}
            
            {/* Sayfalama - Mobil */}
            <Paper sx={{ mt: 2 }}>
              <TablePagination
                component="div"
                count={filteredCustomers.length}
                page={page}
                onPageChange={handleChangePage}
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                rowsPerPageOptions={[10, 25, 50]}
                labelRowsPerPage={t('qrMenuCustomersPage.pagination.page')}
                labelDisplayedRows={({ from, to, count }) => `${from}-${to}/${count}`}
              />
            </Paper>
          </Box>
        )}
        </>
      ) : null}
    </ScrollableContent>
  );
};

export default CustomersPage;
